import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useController, useWatch } from 'react-hook-form';
import { Button, Typography, FormControlLabel, Switch, Checkbox, FormGroup, Divider } from '@mui/material';
import Styles from '@/assets/js/Styles';
import { noop } from '@/utils/functions';
import { useControlledFieldArray } from '@/hooks/useControlledFieldArray';
import { OutlinedContainer } from '@/components/OutlinedContainer';
import { BlockComponent } from '../../../lib/declarations/EditorComponentTypes';
import { BlockPath, useCurrentBlock } from '../../CurrentBlockProvider';
import Container from '../../../../components/Container';
import { BlockSpecificPage } from '../../../lib/declarations/BlockSpecificPage';
import { PageListManualBlock as PageListManualBlockModel } from '../../../../declarations/models/blocks/PageListBlocks';
import M24PageFinder from '../../../../components/Finder/M24PageFinder/M24PageFinder';
import PageListManualBlockItem from './PageListManualBlockItem';
import PageListDisplayOptions from './PageListDisplayOptions';

export const settingsAreaStyle = {
  width: '100%',
  backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
  padding: 2,
  borderRadius: Styles.Dimensions.RADIUS_ROUNDNESS_DEFAULT,
  gap: 2,
};

export const PageListManualBlock: BlockComponent = () => {
  const { blockPath } = useCurrentBlock();
  const { t } = useTranslation('components');
  const [pageFinderOpen, setPageFinderOpen] = useState<boolean>(false);
  const [linkPageFinderOpen, setLinkPageFinderOpen] = useState<boolean>(false);

  const currentViewType = useWatch<BlockSpecificPage<PageListManualBlockModel>, `${BlockPath}.view`>({
    name: `${blockPath}.view`,
  });

  const currentLayout = useWatch<BlockSpecificPage<PageListManualBlockModel>, `${BlockPath}.layout`>({
    name: `${blockPath}.layout`,
  });

  const {
    field: { value: showAllLinkPage, onChange: onChangeShowAllLinkPage },
  } = useController<BlockSpecificPage<PageListManualBlockModel>, `${BlockPath}.showAllLinkPage`>({
    name: `${blockPath}.showAllLinkPage`,
  });

  const {
    field: { value: enableSearch, onChange: setEnableSearch },
  } = useController<BlockSpecificPage<PageListManualBlockModel>, `${BlockPath}.enableSearch`>({
    name: `${blockPath}.enableSearch`,
  });

  const [userFiltering, setUserFiltering] = useState<boolean>(!!enableSearch);

  const handleChangeUserFiltering = () => {
    if (userFiltering) {
      setEnableSearch(false);
    }
    setUserFiltering(!userFiltering);
  };

  const {
    fields: items,
    append,
    move,
    remove,
  } = useControlledFieldArray<BlockSpecificPage<PageListManualBlockModel>, `${BlockPath}.items`>({
    name: `${blockPath}.items`,
  });

  return (
    <Container fullWidth column mt={1} gap={2} left>
      <M24PageFinder
        open={pageFinderOpen}
        onClose={() => setPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          pages.forEach((page) => {
            if (page.source.id) {
              append({ id: page.source.id });
            }
          });
        }}
        multiSelect
      />
      <M24PageFinder
        hideSiteSelector
        open={linkPageFinderOpen}
        onClose={() => setLinkPageFinderOpen(false)}
        onSelectionConfirmed={(pages) => {
          if (pages.length > 0) {
            onChangeShowAllLinkPage(pages[0].source);
          }
        }}
      />

      <OutlinedContainer
        fullWidth
        column
        left
        label={t('PageListManualBlock.PagesLabel')}
        sx={{
          backgroundColor: Styles.Colors.FIXED_SECTION_BACKGROUND_COLOR,
        }}>
        {items?.length === 0 && (
          <Typography sx={{ color: Styles.Colors.DARK_GREY, textAlign: 'center', pt: 1 }} variant='body2'>
            <i>{t('PageListManualBlock.NoPagesSelected')}</i>
          </Typography>
        )}
        <Container fullWidth column p={1} gap={0}>
          {items?.map((item, index) => {
            return (
              <PageListManualBlockItem
                key={String(item.id) + index} // eslint-disable-line react/no-array-index-key
                index={index}
                onReorder={move}
                onDelete={remove}
                color={Styles.Colors.WHITE}
              />
            );
          })}
        </Container>
        <Button variant='contained' color='secondary' sx={{ flex: '1 0 auto' }} onClick={() => setPageFinderOpen(true)}>
          {t('PageListManualBlock.SelectPages')}
        </Button>
      </OutlinedContainer>
      <Divider />
      <PageListDisplayOptions blockPath={blockPath} viewType={currentViewType} layout={currentLayout} />

      <OutlinedContainer fullWidth column left label={t('PageListManualBlock.FilteringOptions')}>
        <FormControlLabel
          control={<Switch checked={userFiltering} onChange={handleChangeUserFiltering} color='secondary' />}
          label={t('EventsBlock.UserFilteringLabel')}
        />
        <FormGroup row>
          <FormControlLabel
            control={<Checkbox checked={!!enableSearch} onChange={(_, v) => setEnableSearch(v)} />}
            disabled={!userFiltering}
            label={t('EventsBlock.EnableSearchLabel')}
          />
        </FormGroup>
      </OutlinedContainer>

      <OutlinedContainer fullWidth column left label={t('EventsBlock.ShowAllLinkPageTitle')}>
        {showAllLinkPage && (
          <PageListManualBlockItem
            index={0}
            onReorder={noop}
            onDelete={() => onChangeShowAllLinkPage(null)}
            overrideItemPath={`${blockPath}.showAllLinkPage`}
            color={Styles.Colors.LIGHT_GREY}
            hideHighlightCheckbox
            hideSkinSelector
            disableReordering
          />
        )}
        <Button onClick={() => setLinkPageFinderOpen(true)} variant='outlined' color='secondary'>
          {t('EventsBlock.ShowAllLinkPageButtonLabel')}
        </Button>
      </OutlinedContainer>
    </Container>
  );
};

export default PageListManualBlock;
